import React, { useEffect, useState } from 'react';
import { FileText, Plus, Trash2, RefreshCw, Save, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select, Drawer } from '@/components/admin/shared';
import RichTextEditor from '@/components/admin/RichTextEditor';

const DOC_TYPE_OPTS = [
  { value: 'privacy',  label: 'Privacy Policy' },
  { value: 'terms',    label: 'Terms of Service' },
  { value: 'cookie',   label: 'Cookie Policy' },
  { value: 'refund',   label: 'Refund Policy' },
  { value: 'shipping', label: 'Shipping Policy' },
  { value: 'custom',   label: 'Custom document' },
];
const STATUS_OPTS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived',  label: 'Archived' },
];

function defaultForm() {
  return { doc_type: 'privacy', title: '', slug: '', body_html: '<p></p>', publish_status: 'draft', show_in_footer: true, footer_order: 100, effective_date: '' };
}

export default function LegalDocumentsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/legal-documents');
      setItems(data || []);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(defaultForm()); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({
      doc_type: it.doc_type,
      title: it.title,
      slug: it.slug,
      body_html: it.body_html || '<p></p>',
      publish_status: it.publish_status,
      show_in_footer: it.show_in_footer,
      footer_order: it.footer_order,
      effective_date: it.effective_date || '',
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      payload.footer_order = Number(payload.footer_order) || 100;
      if (!payload.effective_date) delete payload.effective_date;
      if (editing) {
        await api.put(`/admin/legal-documents/${editing.id}`, payload);
        setMsg('Legal document updated.');
      } else {
        await api.post('/admin/legal-documents', payload);
        setMsg('Legal document created.');
      }
      setOpen(false);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!confirm(`Delete "${it.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/legal-documents/${it.id}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="legal-documents-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Legal Documents</h3>
          <span className="text-[10px] font-mono tracking-[0.24em] uppercase text-[var(--pg-ink-faint)]">{items.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><RefreshCw size={14} /> Refresh</button>
          <button data-testid="legal-doc-new" onClick={startNew} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><Plus size={14} /> New document</button>
        </div>
      </div>

      {msg && <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)]">{msg}</div>}

      <div className="pg-admin-card rounded-[var(--pg-radius-md)] overflow-hidden">
        {loading ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-xs font-mono text-[var(--pg-ink-faint)]">No legal documents yet. Create Privacy, Terms, and others so they appear in the footer.</div>
        ) : (
          <div className="divide-y divide-[var(--pg-border-2)]">
            {items.map((it) => (
              <div key={it.id} className="p-4 flex items-center gap-4" data-testid={`legal-row-${it.slug}`}>
                <button onClick={() => startEdit(it)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-base text-[var(--pg-ink)]">{it.title}</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono tracking-[0.2em] uppercase"
                          style={{
                            background: it.publish_status === 'published' ? 'rgba(25,194,142,0.14)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${it.publish_status === 'published' ? 'rgba(25,194,142,0.5)' : 'rgba(255,255,255,0.2)'}`,
                            color: it.publish_status === 'published' ? 'var(--pg-emerald-2)' : 'var(--pg-ink-muted)',
                          }}>
                      {it.publish_status}
                    </span>
                  </div>
                  <p className="text-[11px] font-mono text-[var(--pg-ink-muted)] mt-1">
                    /{it.slug} · {it.doc_type} · v{it.version} · {it.show_in_footer ? 'in footer' : 'hidden from footer'}
                  </p>
                </button>
                {it.publish_status === 'published' && (
                  <a href={`/legal/${it.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-gold-2)]">
                    <ExternalLink size={16} />
                  </a>
                )}
                <button onClick={() => remove(it)} className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]" aria-label="delete">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={editing ? `Edit “${editing.title}”` : 'New Legal Document'} onSave={save} saving={saving} wide>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Document type" value={form.doc_type} onChange={(v) => setForm({ ...form, doc_type: v })} options={DOC_TYPE_OPTS} />
          <Select label="Publish status" value={form.publish_status} onChange={(v) => setForm({ ...form, publish_status: v })} options={STATUS_OPTS} />
        </div>
        <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Privacy Policy" />
        <TextField label="URL slug (auto if blank) — visitors access at /legal/{slug}" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="privacy-policy" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Effective date (ISO, optional)" value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} placeholder="2026-07-01" />
          <TextField label="Footer sort order" type="number" value={form.footer_order} onChange={(v) => setForm({ ...form, footer_order: v })} />
        </div>
        <Toggle label="Show in site footer" value={form.show_in_footer} onChange={(v) => setForm({ ...form, show_in_footer: v })} />
        <div>
          <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Body</label>
          <div className="mt-1">
            <RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} />
          </div>
        </div>
      </Drawer>
    </div>
  );
}
